import { RETURN_ACTION_LABELS, type OperationLogEntryDto } from '@pietrochip/shared';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useRef } from 'react';
import { personName, time } from '../lib/format';

/**
 * Il registro completo, una riga per operazione.
 *
 * La lista è virtualizzata come quella dei chip, e per la stessa ragione: un
 * evento di una giornata intera accumula centinaia di righe, e disegnarle tutte
 * renderebbe lento lo scorrimento proprio mentre qualcuno sta cercando una
 * restituzione precisa. Si disegna solo quello che sta nello schermo.
 *
 * Le righe sono alte in modo variabile — su un telefono ognuna va su due righe —
 * quindi l'altezza la misura il DOM invece di essere stimata: con una stima
 * fissa la barra di scorrimento mentirebbe sulle liste lunghe.
 */
export function OperationLogList({ operations }: { operations: readonly OperationLogEntryDto[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: operations.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 44,
    overscan: 10,
  });

  const items = virtualizer.getVirtualItems();

  return (
    <div className="op-list">
      <div className="op-list__inner">
        <div className="op-list__head">
          <span>Ora</span>
          <span>Operazione</span>
          <span>Codice</span>
          <span>Atleta</span>
          <span>Squadra</span>
          <span>Categoria</span>
          <span>N. gara</span>
          <span>Operatore</span>
        </div>

        {operations.length === 0 ? (
          <p className="muted" style={{ padding: '18px 14px' }}>
            Nessuna operazione registrata: qui comparirà tutto quello che succede
            all’evento, restituzioni e correzioni comprese.
          </p>
        ) : (
          <div className="op-list__viewport" ref={scrollRef}>
            <div style={{ height: virtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
              {items.map((item) => {
                const entry = operations[item.index];
                if (!entry) return null;

                return (
                  <div
                    key={entry.id}
                    data-index={item.index}
                    ref={virtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${item.start}px)`,
                    }}
                  >
                    <OperationRow entry={entry} />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OperationRow({ entry }: { entry: OperationLogEntryDto }) {
  // Annullare o togliere dall'elenco sono le righe che qualcuno va a cercare
  // *dopo*, quando un totale non torna: portano il colore delle cose mancanti,
  // come nella colonna laterale della schermata di lavoro.
  const reverted = entry.action === 'unreturn' || entry.action === 'remove';

  return (
    <div className={`op-row ${reverted ? 'op-row--reverted' : ''}`}>
      <span className="op-row__time">{time(entry.at)}</span>
      <span className="op-row__action">{RETURN_ACTION_LABELS[entry.action] ?? entry.action}</span>
      <span className="mono op-row__code">{entry.code}</span>

      {/*
        Su schermo largo questi cinque campi sono colonne come le altre — il
        contenitore è `display: contents`, quindi non occupa una casella sua e
        lascia i figli sulla griglia. Su un telefono, dove otto colonne non
        stanno, diventano la seconda riga della voce: una lista di attributi
        della persona, nell'ordine in cui si leggono. Un solo set di celle per
        entrambe le forme, così non c'è niente da tenere allineato.
      */}
      <span className="op-row__meta">
        <span className="op-row__athlete truncate">
          {personName(entry.firstName, entry.lastName)}
        </span>
        <span className="op-row__team truncate">{entry.team ?? '—'}</span>
        <span className="op-row__category truncate">{entry.category ?? '—'}</span>
        <span className="op-row__number">{entry.raceNumber ?? '—'}</span>
        <span className="op-row__operator truncate">{entry.userName ?? '—'}</span>
      </span>
    </div>
  );
}
